import type {
  GraphEntityType,
  GraphFrameExportFormat,
  GraphNode,
  GraphVisualizationFrame
} from "@salesko/contracts";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  HelpCircle,
  Loader2,
  LocateFixed,
  Maximize2,
  Minus,
  Network,
  Plus,
  Save,
  Settings,
  Share2,
  Undo2,
  X
} from "lucide-react";
import { Button, IconButton } from "@salesko/design-system";
import {
  lazy,
  Suspense,
  useRef
} from "react";
import type { GraphViewMode } from "../../components/GraphCanvas";
import { useI18n } from "../../i18n";
import { graphFrameBadgeLabel, graphFrameBadgeTitle } from "../lib/frameBadge";
import { shortChangeSetId } from "../lib/format";
import type { TimelineMoment } from "../lib/timeline";
import type { ChangeSetActionVariables, ChangeSetLifecycleState, FrameIoState } from "../types";

import type { GraphCanvasControl } from "../../components/GraphCanvas";

const GraphCanvas = lazy(() =>
  import("../../components/GraphCanvas").then((module) => ({ default: module.GraphCanvas }))
);

type GraphStageProps = {
  canSaveTimelineReplay: boolean;
  changeSetCreatePending: boolean;
  changeSetState: ChangeSetLifecycleState;
  enabledEntityTypes: GraphEntityType[];
  frame: GraphVisualizationFrame;
  frameIoState: FrameIoState;
  frameSharePending: boolean;
  onApplyChangeSet: (variables: ChangeSetActionVariables) => void;
  onDismissChangeSetStatus: () => void;
  onDismissFrameIoStatus: () => void;
  onFrameShareClick: () => void;
  onHelpClick: () => void;
  onRecenter: () => void;
  onRevertChangeSet: (variables: ChangeSetActionVariables) => void;
  onSettingsClick: () => void;
  onSelectEdge: (edgeId: string) => void;
  onSelectNode: (nodeId: string) => void;
  onSaveTimelineReplay: () => void;
  onTimelineFocusChange: (value: number) => void;
  onTimelineNeedsEventAction: () => void;
  onViewModeChange: (mode: GraphViewMode) => void;
  onZoomChange: (nextZoomLevel: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  readOnly: boolean;
  searchResultIds: string[];
  selectedEdgeId: string | null;
  selectedNode: GraphNode | null;
  spotlightNodeIds: string[];
  timeline: ReturnType<typeof import("../lib/timeline").deriveTimeline>;
  timelineFocus: number;
  viewMode: GraphViewMode;
  zoomLevel: number;
};

export function GraphStage({
  canSaveTimelineReplay,
  changeSetCreatePending,
  changeSetState,
  enabledEntityTypes,
  frame,
  frameIoState,
  frameSharePending,
  onApplyChangeSet,
  onDismissChangeSetStatus,
  onDismissFrameIoStatus,
  onFrameShareClick,
  onHelpClick,
  onRecenter,
  onRevertChangeSet,
  onSettingsClick,
  onSelectEdge,
  onSelectNode,
  onSaveTimelineReplay,
  onTimelineFocusChange,
  onTimelineNeedsEventAction,
  onViewModeChange,
  onZoomChange,
  onZoomIn,
  onZoomOut,
  readOnly,
  searchResultIds,
  selectedEdgeId,
  selectedNode,
  spotlightNodeIds,
  timeline,
  timelineFocus,
  viewMode,
  zoomLevel
}: GraphStageProps) {
  const { t } = useI18n();
  const stageRef = useRef<HTMLElement | null>(null);
  const canvasControlRef = useRef<GraphCanvasControl | null>(null);
  const currentMoment =
    timeline.moments[timelineFocus] ?? timeline.moments[timeline.moments.length - 1] ?? null;
  const currentTimelineIndex = currentMoment?.order ?? 0;
  const selectTimelineMoment = (index: number) => {
    const nextIndex = Math.min(
      Math.max(Math.round(index), 0),
      Math.max(0, timeline.moments.length - 1)
    );
    const moment = timeline.moments[nextIndex];
    if (!moment) {
      return;
    }
    onTimelineFocusChange(nextIndex);
    onSelectNode(moment.event.id);
  };
  const handleTimelineModeClick = () => {
    if (!timeline.enabled || !currentMoment) {
      return;
    }
    onViewModeChange("timeline");
    selectTimelineMoment(currentMoment.order);
  };
  const frameBadgeLabel = graphFrameBadgeLabel(frame, t.graphStage.relationshipGraphLabel);
  const frameBadgeTitle = graphFrameBadgeTitle(frame, t.graphStage.relationshipGraphLabel);
  const handleFullscreenClick = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }

    void stageRef.current?.requestFullscreen();
  };

  return (
    <main className="graph-stage" aria-label={t.graphStage.graphFieldAriaLabel} ref={stageRef}>
      <div className="graph-toolbar">
        <div className="view-toggle" role="group" aria-label={t.graphStage.viewModeLabel}>
          <button
            type="button"
            className={viewMode === "flat" ? "view-toggle-option is-active" : "view-toggle-option"}
            aria-pressed={viewMode === "flat"}
            onClick={() => onViewModeChange("flat")}
            title={t.graphStage.show2dTitle}
          >
            <Network size={16} />
            <span>2D</span>
          </button>
          <button
            type="button"
            className={viewMode === "timeline" ? "view-toggle-option is-active" : "view-toggle-option"}
            aria-pressed={viewMode === "timeline"}
            disabled={!timeline.enabled}
            title={timeline.enabled ? t.graphStage.timelineEnabledTitle : t.graphStage.timelineDisabledTitle}
            onClick={handleTimelineModeClick}
          >
            <Clock size={16} />
            <span>{t.graphStage.timelineLabel}</span>
          </button>
        </div>
        <IconButton
          ariaLabel={t.graphStage.createShareAriaLabel}
          disabled={readOnly || frameSharePending}
          icon={frameIoState.status === "sharing" ? <Loader2 className="spin" size={17} /> : <Share2 size={17} />}
          onClick={onFrameShareClick}
          title={readOnly ? t.graphStage.sharedReadOnlyTitle : t.graphStage.copyShareTitle}
        />
        <IconButton
          ariaLabel={t.graphStage.openSettingsAriaLabel}
          icon={<Settings size={18} />}
          onClick={onSettingsClick}
          title={t.graphStage.openSettingsTitle}
        />
        <IconButton
          ariaLabel={t.graphStage.openGuideAriaLabel}
          icon={<HelpCircle size={18} />}
          onClick={onHelpClick}
          title={t.graphStage.openGuideTitle}
        />
        <IconButton
          ariaLabel={t.graphStage.toggleFullscreenAriaLabel}
          icon={<Maximize2 size={18} />}
          onClick={handleFullscreenClick}
          title={t.graphStage.toggleFullscreenTitle}
        />
      </div>

      {!timeline.enabled ? (
        <div className="timeline-unavailable" role="status">
          <span>{t.graphStage.timelineNeedsTwoEvents}</span>
          <button
            className="timeline-unavailable-action"
            onClick={onTimelineNeedsEventAction}
            type="button"
          >
            {t.graphStage.addDatedInteraction}
          </button>
        </div>
      ) : null}

      <FrameIoStatus state={frameIoState} onDismiss={onDismissFrameIoStatus} />

      {readOnly ? null : (
        <ChangeSetLifecycleStatus
          state={changeSetState}
          onApply={onApplyChangeSet}
          onDismiss={onDismissChangeSetStatus}
          onRevert={onRevertChangeSet}
        />
      )}

      <Suspense
        fallback={
          <div className="graph-canvas">
            <div className="graph-empty-state">
              <span className="graph-empty-mark" />
              <strong>{t.graphStage.relationshipGraphLabel}</strong>
              <p>{t.graphStage.loadingGraphField}</p>
            </div>
          </div>
        }
      >
        <GraphCanvas
          canvasControlRef={canvasControlRef}
          enabledEntityTypes={enabledEntityTypes}
          focusNodeId={selectedNode?.id ?? frame.camera.focusNodeId ?? null}
          frame={frame}
          matchNodeIds={searchResultIds}
          onSelectEdge={onSelectEdge}
          onSelectNode={onSelectNode}
          onZoomChange={onZoomChange}
          selectedEdgeId={selectedEdgeId}
          selectedNodeId={selectedNode?.id ?? null}
          spotlightNodeIds={spotlightNodeIds}
          timelineMoment={
            currentMoment
              ? {
                  eventId: currentMoment.event.id,
                  attachedNodeIds: currentMoment.attached.map((node) => node.id)
                }
              : null
          }
          viewMode={viewMode}
          zoomLevel={zoomLevel}
        />
      </Suspense>

      {viewMode === "timeline" && timeline.enabled ? (
        <div className="time-scrubber">
          <button
            aria-label={t.graphStage.previousTimelineMoment}
            className="timeline-step-button"
            disabled={currentTimelineIndex === 0}
            onClick={() => selectTimelineMoment(currentTimelineIndex - 1)}
            type="button"
          >
            <ChevronLeft size={17} />
          </button>
          <div
            aria-label={t.graphStage.timelineMomentsAriaLabel}
            className="timeline-moment-rail"
            onKeyDown={(event) => {
              if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
                return;
              }
              event.preventDefault();
              selectTimelineMoment(
                currentTimelineIndex + (event.key === "ArrowLeft" ? -1 : 1)
              );
            }}
            role="navigation"
          >
            {timeline.moments.map((moment, index) => {
              const linkedCount = moment.attached.filter((node) =>
                enabledEntityTypes.includes(node.type)
              ).length;
              const relativeTime = momentRelativeTimeText(moment);
              const isActive = index === currentTimelineIndex;
              return (
                <button
                  aria-current={isActive ? "step" : undefined}
                  className={
                    isActive
                      ? "timeline-moment-button is-active"
                      : "timeline-moment-button"
                  }
                  key={moment.event.id}
                  onClick={() => selectTimelineMoment(index)}
                  type="button"
                >
                  <span className="timeline-moment-date">
                    {momentDateLabel(moment, t.graphStage.dateUnknown)}
                  </span>
                  <strong>{moment.event.label}</strong>
                  {relativeTime ? (
                    <span className="timeline-moment-relative">
                      {relativeTime}
                    </span>
                  ) : null}
                  <span className="timeline-moment-meta">
                    {t.graphStage.timelinePosition(index + 1, timeline.moments.length)}
                    {" · "}
                    {linkedCount > 0
                      ? t.graphStage.linkedEntityCount(linkedCount)
                      : t.graphStage.noLinkedEntities}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            aria-label={t.graphStage.nextTimelineMoment}
            className="timeline-step-button"
            disabled={currentTimelineIndex >= timeline.moments.length - 1}
            onClick={() => selectTimelineMoment(currentTimelineIndex + 1)}
            type="button"
          >
            <ChevronRight size={17} />
          </button>
          {canSaveTimelineReplay || changeSetCreatePending ? (
            <button
              className="secondary-button time-scrubber-save"
              disabled={changeSetCreatePending}
              onClick={onSaveTimelineReplay}
              title={t.graphStage.setOpeningMomentTitle}
              type="button"
            >
              {changeSetCreatePending ? (
                <Loader2 className="spin" size={14} />
              ) : (
                <Save size={14} />
              )}
              <span>{t.graphStage.setOpeningMoment}</span>
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="zoom-stack" aria-label={t.graphStage.graphControlsAriaLabel}>
        <button
          className="icon-button"
          title={t.graphStage.recenter}
          type="button"
          aria-label={t.graphStage.recenter}
          onClick={() => {
            canvasControlRef.current?.recenter();
            onRecenter();
          }}
        >
          <LocateFixed size={18} />
        </button>
        <button className="icon-button" title={t.graphStage.zoomIn} type="button" aria-label={t.graphStage.zoomIn} onClick={onZoomIn}>
          <Plus size={18} />
        </button>
        <button className="icon-button" title={t.graphStage.zoomOut} type="button" aria-label={t.graphStage.zoomOut} onClick={onZoomOut}>
          <Minus size={18} />
        </button>
      </div>

      <div className="contract-strip" aria-label={frameBadgeTitle} title={frameBadgeTitle}>
        <span>{frameBadgeLabel}</span>
      </div>
    </main>
  );
}

function FrameIoStatus({ onDismiss, state }: { onDismiss: () => void; state: FrameIoState }) {
  const { t } = useI18n();
  if (state.status === "idle") {
    return null;
  }

  const dismiss = canDismissFrameIoState(state.status) ? (
    <StatusDismissButton label={t.frameIo.dismissStatus} onDismiss={onDismiss} />
  ) : null;

  if (state.status === "exporting") {
    return (
      <div className="frame-io-status is-pending" role="status" aria-live="polite">
        <Loader2 className="spin" size={16} />
        <span className="frame-io-status-message">{t.frameIo.exporting(frameIoFormatLabel(state.format))}</span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "importing") {
    return (
      <div className="frame-io-status is-pending" role="status" aria-live="polite">
        <Loader2 className="spin" size={16} />
        <span className="frame-io-status-message">
          {t.frameIo.importing(state.fileName, frameIoFormatLabel(state.format))}
        </span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "sharing") {
    return (
      <div className="frame-io-status is-pending" role="status" aria-live="polite">
        <Loader2 className="spin" size={16} />
        <span className="frame-io-status-message">{t.frameIo.creatingShareLink}</span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "error") {
    const prefix = state.fileName ? `${state.fileName}: ` : "";
    return (
      <div className="frame-io-status is-error" role="alert">
        <AlertCircle size={16} />
        <span className="frame-io-status-message">{`${prefix}${state.message}`}</span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "exported") {
    return (
      <div className="frame-io-status is-success" role="status" aria-live="polite">
        <Download size={16} />
        <span className="frame-io-status-message">
          {t.frameIo.downloaded(frameIoFormatLabel(state.format))} <code>{state.filename}</code>.
        </span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "shared") {
    return (
      <div className="frame-io-status is-success" role="status" aria-live="polite">
        <Share2 size={16} />
        <span className="frame-io-status-message">
          {state.copied ? t.frameIo.copiedShareLink : t.frameIo.shareLinkReadyNoCopy} ·
          {t.frameIo.shareExpires(state.expiresAt.slice(0, 10))}
        </span>
        {dismiss}
      </div>
    );
  }

  return (
    <div className="frame-io-status is-success" role="status" aria-live="polite">
      <CheckCircle2 size={16} />
      <span className="frame-io-status-message">
        {t.frameIo.importedSummary(state.fileName, state.importSummary.nodeCount, state.importSummary.edgeCount)}{" "}
        <code>{shortChangeSetId(state.commitId)}</code>.
        {frameIoWarningsText(state.importSummary.warnings, t)}
      </span>
      {dismiss}
    </div>
  );
}

function ChangeSetLifecycleStatus({
  onApply,
  onDismiss,
  onRevert,
  state
}: {
  onApply: (variables: ChangeSetActionVariables) => void;
  onDismiss: () => void;
  onRevert: (variables: ChangeSetActionVariables) => void;
  state: ChangeSetLifecycleState;
}) {
  const { t } = useI18n();
  if (state.status === "idle") {
    return null;
  }

  const dismiss = canDismissChangeSetState(state.status) ? (
    <StatusDismissButton label={t.changeSet.dismissStatus} onDismiss={onDismiss} />
  ) : null;

  if (state.status === "importing") {
    return (
      <div className="change-set-status is-pending" role="status" aria-live="polite">
        <Loader2 className="spin" size={16} />
        <span className="change-set-status-message">{t.changeSet.importingFile(state.fileName)}</span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "creating") {
    return (
      <div className="change-set-status is-pending" role="status" aria-live="polite">
        <Loader2 className="spin" size={16} />
        <span className="change-set-status-message">{t.changeSet.creatingFor(state.sourceLabel)}</span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="change-set-status is-error" role="alert">
        <AlertCircle size={16} />
        <span className="change-set-status-message">
          {state.fileName ? `${state.fileName}: ${state.message}` : state.message}
        </span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "pending") {
    const changeSet = state.response.changeSet;
    const importSummary = state.importSummary;
    return (
      <div className="change-set-status is-success" role="status" aria-live="polite">
        <CheckCircle2 size={16} />
        <span className="change-set-status-message">
          {t.changeSet.pendingSummary(
            shortChangeSetId(changeSet.id),
            state.sourceLabel,
            importSummary?.commandCount ?? changeSet.commandCount,
            importSummary?.nodeCount,
            importSummary?.edgeCount
          )}
        </span>
        <span className="change-set-actions">
          <button
            className="change-set-action"
            onClick={() =>
              onApply({
                changeSetId: changeSet.id,
                frameRecordId: changeSet.frameRecordId,
                ...(importSummary ? { importSummary } : {}),
                sourceLabel: state.sourceLabel
              })
            }
            type="button"
          >
            <CheckCircle2 size={14} />
            <span>{t.changeSet.apply}</span>
          </button>
        </span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "applying" || state.status === "reverting") {
    return (
      <div className="change-set-status is-pending" role="status" aria-live="polite">
        <Loader2 className="spin" size={16} />
        <span className="change-set-status-message">
          {state.status === "applying"
            ? t.changeSet.applying(shortChangeSetId(state.changeSetId), state.sourceLabel)
            : t.changeSet.reverting(shortChangeSetId(state.changeSetId), state.sourceLabel)}
        </span>
        {dismiss}
      </div>
    );
  }

  if (state.status === "applied") {
    const changeSet = state.response.changeSet;
    return (
      <div className="change-set-status is-success" role="status" aria-live="polite">
        <CheckCircle2 size={16} />
        <span className="change-set-status-message">
          {t.changeSet.applied(shortChangeSetId(changeSet.id), state.response.projectionStatus)}
        </span>
        <span className="change-set-actions">
          <button
            className="change-set-action"
            onClick={() =>
              onRevert({
                changeSetId: changeSet.id,
                frameRecordId: changeSet.frameRecordId,
                ...(state.importSummary ? { importSummary: state.importSummary } : {}),
                sourceLabel: state.sourceLabel
              })
            }
            type="button"
          >
            <Undo2 size={14} />
            <span>{t.changeSet.revert}</span>
          </button>
        </span>
        {dismiss}
      </div>
    );
  }

  return (
    <div className="change-set-status is-success" role="status" aria-live="polite">
      <Undo2 size={16} />
      <span className="change-set-status-message">
        {t.changeSet.reverted(shortChangeSetId(state.response.changeSet.id), state.response.projectionStatus)}
      </span>
      {dismiss}
    </div>
  );
}

function StatusDismissButton({ label, onDismiss }: { label: string; onDismiss: () => void }) {
  const { t } = useI18n();
  return (
    <button className="status-dismiss-button" type="button" aria-label={label} title={t.common.dismiss} onClick={onDismiss}>
      <X size={14} />
    </button>
  );
}

function canDismissFrameIoState(status: FrameIoState["status"]) {
  return status === "error" || status === "exported" || status === "imported" || status === "shared";
}

function canDismissChangeSetState(status: ChangeSetLifecycleState["status"]) {
  return status === "error" || status === "pending" || status === "applied" || status === "reverted";
}

function frameIoFormatLabel(format: GraphFrameExportFormat) {
  return format === "obsidian" ? "Obsidian" : "JSON";
}

function frameIoWarningsText(warnings: string[], t: ReturnType<typeof useI18n>["t"]) {
  if (warnings.length === 0) {
    return "";
  }

  return t.graphStage.warningsCount(warnings.length);
}

function momentDateLabel(moment: TimelineMoment, unknownDateLabel: string) {
  if (moment.occurredAt) {
    return moment.occurredAt.slice(0, 10); // YYYY-MM-DD
  }
  return unknownDateLabel;
}

function momentRelativeTimeText(moment: TimelineMoment) {
  const timeText = moment.event.data.timeText;
  return typeof timeText === "string" ? timeText : "";
}
